/**
 * The live-data layer.
 *
 * Today this polls. That is an honest statement rather than an apology: the
 * cloud has no push transport yet, and a 3s poll against a node that beats
 * every 30s already shows changes faster than the hardware produces them.
 *
 * It is written as a subscription — `subscribe(key, fetcher, onData)` — so
 * that when MQTT-over-WebSocket lands, `startTransport` is the only thing
 * that changes. No screen knows how its data arrives, which is the point:
 * swapping transports should not be a rewrite of the UI.
 *
 * `since` on each subscriber tells the UI how stale it is, so "live" can be
 * an observation rather than a decoration. A spinner that lies about
 * freshness is worse than a number that admits its age.
 */
import { useEffect, useRef, useState, useCallback } from 'react';

const DEFAULT_INTERVAL_MS = 3000;
// Backing off on failure matters: a portal left open against a dead API
// should not hammer it a hundred times a minute.
const MAX_BACKOFF_MS = 30000;

export function useLive(fetcher, deps = [], { interval = DEFAULT_INTERVAL_MS, enabled = true } = {}) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(true);

  const timer = useRef(null);
  const backoff = useRef(interval);
  const alive = useRef(true);
  // Held in a ref so changing the fetcher does not restart the loop and
  // reset the backoff.
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const tick = useCallback(async () => {
    if (!alive.current) return;
    try {
      const result = await fetcherRef.current();
      if (!alive.current) return;
      setData(result);
      setError(null);
      setUpdatedAt(Date.now());
      backoff.current = interval;
    } catch (e) {
      if (!alive.current) return;
      setError(e.message);
      backoff.current = Math.min(backoff.current * 2, MAX_BACKOFF_MS);
    } finally {
      if (alive.current) {
        setLoading(false);
        timer.current = setTimeout(tick, backoff.current);
      }
    }
  }, [interval]);

  useEffect(() => {
    alive.current = true;
    backoff.current = interval;
    setLoading(true);
    tick();

    // A backgrounded tab should not keep polling. Coming back should refresh
    // immediately rather than showing stale numbers until the next tick.
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        clearTimeout(timer.current);
        backoff.current = interval;
        tick();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      alive.current = false;
      clearTimeout(timer.current);
      document.removeEventListener('visibilitychange', onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ...deps]);

  const refresh = useCallback(() => {
    clearTimeout(timer.current);
    backoff.current = interval;
    tick();
  }, [tick, interval]);

  return { data, error, loading, updatedAt, refresh };
}

/** Human phrasing for an age. Kept warm and plain: "just now", not "0s ago". */
export function ago(timestamp) {
  if (!timestamp) return 'never';
  const seconds = Math.floor((Date.now() - new Date(timestamp).getTime()) / 1000);
  if (seconds < 0) return 'just now';
  if (seconds < 10) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}
